import { createSelector } from '@reduxjs/toolkit';
import { produce } from 'immer';
import { AuthType, getAuthType } from 'services/session/session.helpers';
import {
  selectApiAuthHeaders,
  selectSessionInfo,
} from 'services/session/session.selectors';
import { withCurrentStore } from 'store/currentStore';
import { AppState, ReduxSelector } from 'store/types';
import { v4 as uuidV4 } from 'uuid';
import { buildSsoEndpoint, getCommonHeaders } from './api.helpers';
import {
  ApiRequestSuccessInfo,
  RequestConfig,
  RequestLoadingState,
} from './api.types';

export const selectApiState = (reduxState: AppState) => reduxState.api;

/**
 * Outputs a selector that returns 'true' if we are fetching the resource.
 * @param resourceId Unique resource identifier.
 */
export const makeSelectIsRequestPending =
  (resourceId: string) => (reduxState: AppState) =>
    !!selectApiState(reduxState).pending[resourceId];

/**
 * Returns 'true' if the request has failed.
 * @param resourceId Unique resource identifier.
 */
export const makeSelectHasRequestFailed =
  (resourceId: string) => (reduxState: AppState) =>
    !!selectApiState(reduxState).failed[resourceId];

/**
 * Given `resourceId` selects the status code of the response if is present,
 * or `0`.
 * @param resourceId
 */
export const makeSelectApiResponseStatusOf =
  (resourceId: string) =>
  (reduxState: AppState): number => {
    const successResponseStatus: number | undefined =
      selectApiState(reduxState).loaded[resourceId]?.response?.status;

    if (successResponseStatus) {
      return successResponseStatus;
    }

    const errorResponseStatus: number | undefined =
      selectApiState(reduxState).failed[resourceId]?.response?.status;

    return errorResponseStatus ?? 0;
  };

/**
 * Returns 'true' if we have fetched the resource.
 * @param resourceId Unique resource identifier.
 */
export const makeSelectIsRequestLoaded =
  (resourceId: string) => (reduxState: AppState) =>
    !!selectApiState(reduxState).loaded[resourceId];

/**
 * Returns 'true' if we have fetched the resource and
 * it has been marked as invalidated.
 * @param resourceId Unique resource identifier.
 */
export const makeSelectHasResourceBeenInvalidated =
  (resourceId: string) => (reduxState: AppState) => {
    const maybeDefinedLoadedRequest: ApiRequestSuccessInfo | undefined =
      selectApiState(reduxState).loaded[resourceId];

    if (!maybeDefinedLoadedRequest) {
      return false;
    }

    return maybeDefinedLoadedRequest.didInvalidate;
  };

/**
 * Given a 'resourceId' outputs a selector that extracts the loading state of
 * identified by the input.
 * @param resourceId Unique resource identifier.
 * @see RequestLoadingState definition in './api.types.d.ts'.
 */
export const makeSelectResourceLoadingState: (
  resourceId: string,
) => ReduxSelector<RequestLoadingState> = (resourceId: string) =>
  createSelector(
    makeSelectIsRequestLoaded(resourceId),
    makeSelectHasResourceBeenInvalidated(resourceId),
    makeSelectHasRequestFailed(resourceId),
    (isLoaded, isInvalidated, hasFailed) => ({
      isLoaded,
      isInvalidated,
      hasFailed,
    }),
  );
/**
 * Given a 'resourceId' outputs a selector that extracts the response data or error
 * identified by the input.
 * @param resourceId Unique resource identifier.
 */
export const makeSelectResourceResponse: <TData, TError>(
  resourceId: string,
) => ReduxSelector<{ data?: TData; error?: TError }> = (resourceId: string) =>
  createSelector(
    (state) => state.api.loaded[resourceId],
    (state) => state.api.failed[resourceId],
    (loaded, failed) => ({
      data: loaded?.response?.data,
      error: failed?.response?.data,
    }),
  );

/**
 * We use this for the RTK queries and mutations so we don't have to use withCurrentStore every time
 * maybe there's a better solution though
 */
export function makeSelectAuthApiConfigWithStore(requestConfig: RequestConfig) {
  return makeSelectAuthApiConfig(requestConfig)(
    withCurrentStore((store) => store.getState()),
  );
}

export function makeSelectAuthApiConfig(requestConfig: RequestConfig) {
  return (reduxState: AppState) => {
    const requestId = uuidV4();

    const authHeaders = selectApiAuthHeaders(reduxState);

    const { ipAddress, channel, channelInfo } = selectSessionInfo(reduxState);

    return produce(requestConfig, (draft) => {
      if (
        ['POST', 'PATCH', 'PUT'].includes(requestConfig.method?.toUpperCase())
      ) {
        if (!draft.data) {
          draft.data = {};
        }
      } else if (
        ['GET', 'HEAD'].includes(requestConfig.method?.toUpperCase())
      ) {
        if (!draft.params) {
          draft.params = {};
        }
      }

      // In order to support stateless JWT, always allow SSO URL rewrite
      // unless explicitly disabled by the caller.
      const allowSsoUrlRewrite = requestConfig.allowSsoUrlRewrite ?? true;
      if (allowSsoUrlRewrite) {
        const authType = getAuthType();
        if (authType === AuthType.Sso) {
          draft.url = buildSsoEndpoint(requestConfig.url);
        }
      }

      // The new "core" services have common data in the headers, while the legacy
      // ones have it in the request body / params. Until the migration is complete,
      // this behavior defaults to true.
      // enrichRequestData = true -> common data in body
      // enrichRequestData = false -> common data in headers
      const enrichRequestData = requestConfig.enrichRequestData ?? true;

      draft.headers = {
        ...(enrichRequestData ? {} : getCommonHeaders(reduxState)),
        ...authHeaders,
        ...requestConfig.headers,
      };

      if (enrichRequestData) {
        if (draft.data) {
          if (draft.data instanceof FormData) {
            draft.data.append('ipAddress', ipAddress ?? '');
            draft.data.append('channel', channel?.toString() ?? '');
            draft.data.append('requestId', requestId);
            draft.data.append('infoChannel', channelInfo ?? '');
          } else {
            draft.data.ipAddress = ipAddress;
            draft.data.channel = channel;
            draft.data.requestId = requestId;
            draft.data.infoChannel = channelInfo;
          }
        }

        if (draft.params) {
          draft.params.ipAddress = ipAddress;
          draft.params.channel = channel;
          draft.params.requestId = requestId;
          draft.params.infoChannel = channelInfo;
        }
      }
    });
  };
}
